import { useState, useEffect, useMemo } from 'react';
import { isEqual } from 'lodash';

import { useLocalForage } from 'src/services/storage/use-local-forage';
import { Auth } from 'src/services/api/auth';

type DbModelType<T> = {
    expiresAt?: Date;
    value: T;
}

// this needed only for typing purposes, editors like VSCode or WebStorm
// sometimes have problems with generics in function return types
type Data<T> = T
type SetValue<T> = (v: T, expiredAt?: Date) => void
type DeleteItemFromStorage = () => void
type IsReady = boolean

type UsePersistentStateReturnType<T> = [Data<T>, SetValue<T>, DeleteItemFromStorage, IsReady]

const isDbModelType = <T>(obj: any): obj is DbModelType<T> => {
    return obj && typeof obj === 'object' && 'value' in obj && 'expiresAt' in obj;
};

/**
 * The optional validator function can be used to validate the loaded value from the storage.
 * If the validator returns false, the defaultValue will be used instead.
 *
 * Note: the validator is only called when loading the value from the storage,
 * it is not called when setting a new value.
 */
const CUSTOM_EVENT_NAME = '$reffie_change' as const;

const _cache: Record<string, any> = {};
export const usePersistentState = <T>(key: string, defaultValue: T, validator?: (value: T) => value is T): UsePersistentStateReturnType<T> => {

    const userId = Auth.loggedInUser?.id.toString();
    const {getItem, setItem, removeItem} = useMemo(() => useLocalForage(userId), [userId]);
    const [isReady, setIsReady] = useState(false);

    const [value, _setValue] = useState<T>(_cache[key] ?? defaultValue);

    const setValue = (newValue: T, expiresAt?: Date) => {
        _cache[key] = newValue;
        _setValue(newValue);
        setItem(key, {
            value: newValue,
            expiresAt,
        });
        if (!isEqual(newValue, value)) {
            if ('requestIdleCallback' in window) {
                requestIdleCallback(() => document.dispatchEvent(new CustomEvent(CUSTOM_EVENT_NAME, {detail: {key}})));
            } else {
                // fallback for browsers that do not support requestIdleCallback
                setTimeout(() => document.dispatchEvent(new CustomEvent(CUSTOM_EVENT_NAME, {detail: {key}})), 4);
            }
        }
    };

    useEffect(() => {
        const onStorageChange = (e: CustomEvent) => {
            if (e?.detail?.key === key && !isEqual(_cache[key], value)) {
                setValue(_cache[key]);
            }
        };

        document.addEventListener(CUSTOM_EVENT_NAME, onStorageChange as EventListener);
        return () => {
            document.removeEventListener(CUSTOM_EVENT_NAME, onStorageChange as EventListener);
        };
    }, [value]);

    useEffect(() => {
        (async () => {
            try {
                if (key in _cache) {
                    setValue(_cache[key]);
                    return;
                }
                const storedValue = await getItem(key) as DbModelType<T> | null;
                if (!storedValue) {
                    setValue(defaultValue);
                    return;
                }

                if (!isDbModelType<T>(storedValue)) {
                    setValue(defaultValue);
                    return;
                }

                if (validator && !validator(storedValue.value)) {
                    setValue(defaultValue);
                    return;
                }
                if (storedValue.expiresAt && new Date(storedValue.expiresAt) < new Date()) {
                    setValue(defaultValue);
                } else {
                    setValue(storedValue.value, storedValue.expiresAt);
                }
            } catch {
                setValue(defaultValue);
            } finally {
                setIsReady(true);
            }
        })();
    }, []);

    const _removeItem = () => {
        removeItem(key);
        delete _cache[key];
    };

    return [value, setValue, _removeItem, isReady];
};
